import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AttentionIcon,
  CopyToClipboard,
  RefreshSpinner,
} from 'shared/components/ui/ProjectIcons';
import { toast } from 'react-toastify';
import { Connection, Source } from 'shared/types/ObjectTypes';
import NoResults from 'shared/components/ui/NoResults';
import Pagination from 'shared/components/ui/Pagination';
import { page_size } from 'shared/api/apiUtils';
import { useTimezone } from 'shared/context/TimezoneContext';
import { formatSourceDatetime } from 'sources/SourceHelperFunctions';
import {
  tableClasses,
  paginationClasses,
  filterClasses,
  buttonClasses,
  onTealText,
} from 'styles/tailwindClassnames';
import GlobalSearch from 'shared/components/inputs/GlobalSearch';
import SelectInput from 'shared/components/inputs/SelectInput';
import SortByDropdown, {
  SortOption,
} from 'shared/components/inputs/SortByDropdown';
import SourceDrawer from './sourceFilters/SourceDrawer';
import AddDeviceModal from './sourceFilters/AddDeviceModal';
import ManualBadge from './sourceFilters/ManualBadge';
import {
  SortableHeading,
  ariaSortFor,
  sortDirectionFor,
} from 'shared/components/ui/SortArrow';

type FilterOption = { label: string; value: string };

// The row's controls share its width equally, down to a size that still reads;
// past that they wrap onto the next line, so an active filter is never hidden.
const filterWidth = 'min-w-[10rem] flex-1';

export type RouteSourceProps = {
  sources: Source[];
  totalSources: number;
  globalSearchStr: string;
  take: number;
  offset: number;
  onPageClicked: Function;
  loadingSources: boolean;
  pageLoaded: boolean;
  destinationUrls: string[];
  loadingDestinationUrls: boolean;
  destinationUrlFilterValue: FilterOption | null;
  onChangeGlobalSourceSearch: (value: string) => void;
  onRefresh: () => void;
  onChangeDestinationUrlFilter: Function;
  // Opens one of the Route's destinations. The Route owns the navigation —
  // the URL it builds keeps the id the page is addressed by.
  onViewDestination: (destinationId: string) => void;
  ordering: string;
  onChangeSortBy: (selected: SortOption) => void;
  // Adding a device by hand (GUNDI-5178). The parent owns the request and infers
  // the provider from the route.
  onCreateSource?: (payload: {
    external_id: string;
    name?: string;
  }) => Promise<void>;
};

const RouteDetailsSource = ({
  sources,
  totalSources,
  globalSearchStr,
  take,
  offset,
  onPageClicked,
  pageLoaded,
  loadingSources,
  destinationUrls,
  loadingDestinationUrls,
  destinationUrlFilterValue,
  onChangeGlobalSourceSearch,
  onRefresh,
  onChangeDestinationUrlFilter,
  onViewDestination,
  ordering,
  onChangeSortBy,
  onCreateSource,
}: RouteSourceProps) => {
  const { t } = useTranslation();
  // The view slide-out (null = closed); creating goes through the shared modal.
  const [drawerSource, setDrawerSource] = useState<Source | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const { timezone } = useTimezone();

  const sortOptions: SortOption[] = useMemo(
    () => [
      { label: t('External ID (A-Z)'), value: 'external_id' },
      { label: t('External ID (Z-A)'), value: '-external_id' },
      { label: t('Newest First'), value: '-created_at' },
      { label: t('Oldest First'), value: 'created_at' },
    ],
    [t],
  );

  const hasActiveFilters = !!(globalSearchStr || destinationUrlFilterValue);

  const clearAllFilters = () => {
    onChangeGlobalSourceSearch('');
    onChangeDestinationUrlFilter(null);
  };

  return (
    <div className="mt-8">
      <div className="my-3 flex flex-col">
        {/* Search row */}
        <div className="mb-3 flex flex-row items-end gap-3">
          <div className="flex min-w-0 flex-1 flex-row flex-wrap items-end gap-x-3">
            <div className={filterWidth}>
              <GlobalSearch
                onChangeGlobalSearch={onChangeGlobalSourceSearch}
                placeholder={t('Name')}
                savedString={globalSearchStr}
                fullWidth
              />
            </div>
            <SortByDropdown
              options={sortOptions}
              ordering={ordering}
              onChangeSortBy={onChangeSortBy}
              widthClassName={filterWidth}
            />
            <div className={`mb-2 flex flex-col gap-1 ${filterWidth}`}>
              <span className={filterClasses.label}>{t('Destination')}</span>
              <SelectInput
                name="destination_url__in"
                options={destinationUrls.map((url) => {
                  let hostname = url;
                  try {
                    hostname = new URL(url).hostname;
                  } catch {}
                  return { label: hostname, value: url };
                })}
                value={destinationUrlFilterValue}
                onChange={onChangeDestinationUrlFilter}
                isMulti={false}
                isClearable={true}
                isSearchable={true}
                isDisabled={false}
                isLoading={loadingDestinationUrls}
                className="w-full"
                variant="routeFilter"
              />
            </div>
          </div>
          {hasActiveFilters && (
            <button
              className={`${buttonClasses.clearFilters} mb-2 whitespace-nowrap`}
              onClick={clearAllFilters}
            >
              {t('Clear All Filters')}
            </button>
          )}
        </div>
        <div className="flex w-full flex-col">
          <div className="flex w-full flex-row items-end justify-between gap-3">
            <label className={`${onTealText} transition-colors duration-300`}>
              {t('Total Sources')}: {totalSources}
            </label>
            <div className="flex flex-row items-center gap-3">
              <button
                type="button"
                onClick={() => onRefresh()}
                aria-label={t('Refresh list')}
                disabled={loadingSources}
                className={`flex h-9 w-8 cursor-pointer items-center justify-center ${loadingSources ? 'origin-center animate-spin' : ''} transition-colors duration-300`}
              >
                <RefreshSpinner />
              </button>
              {onCreateSource && (
                <button
                  type="button"
                  onClick={() => setShowCreate(true)}
                  className={`${buttonClasses.primaryAction} whitespace-nowrap`}
                  data-testid="RouteDetailsSources-add"
                >
                  {t('Add Source')}
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className={`mt-3 ${tableClasses.container} cursor-pointer`}>
              <thead className={tableClasses.header}>
                <tr>
                  <th
                    className={tableClasses.headerCell}
                    aria-sort={ariaSortFor(
                      sortDirectionFor(ordering, 'external_id'),
                    )}
                  >
                    <SortableHeading
                      direction={sortDirectionFor(ordering, 'external_id')}
                    >
                      {t('External ID')}
                    </SortableHeading>
                  </th>
                  <th className={tableClasses.headerCell}>{t('Name')}</th>
                  <th className={tableClasses.headerCell}>
                    {t('Destination')}
                  </th>
                  <th
                    className={tableClasses.headerCell}
                    aria-sort={ariaSortFor(
                      sortDirectionFor(ordering, 'created_at'),
                    )}
                  >
                    <SortableHeading
                      direction={sortDirectionFor(ordering, 'created_at')}
                    >
                      {t('Date Created')}
                    </SortableHeading>
                  </th>
                </tr>
              </thead>
              {loadingSources ? (
                <tbody
                  className="w-full text-body-sm"
                  role="status"
                  aria-busy="true"
                >
                  <tr className="sr-only">
                    <td>Loading...</td>
                  </tr>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className={tableClasses.row}>
                      <td className={tableClasses.cell}>
                        <div
                          className={`h-4 ${i % 3 === 0 ? 'w-44' : i % 3 === 1 ? 'w-32' : 'w-52'} animate-pulse rounded bg-teal-t30`}
                        />
                      </td>
                      <td className={tableClasses.cell}>
                        <div className="h-4 w-24 animate-pulse rounded bg-teal-t30" />
                      </td>
                      <td className={tableClasses.cell}>
                        <div
                          className={`h-4 ${i % 3 === 0 ? 'w-48' : i % 3 === 1 ? 'w-36' : 'w-40'} animate-pulse rounded bg-teal-t30`}
                        />
                      </td>
                      <td className={tableClasses.cell}>
                        <div className="h-4 w-32 animate-pulse rounded bg-teal-t30" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : sources.length < 1 ? (
                <tbody>
                  <tr>
                    <td colSpan={4} className="py-16">
                      <NoResults
                        headerMsg={t('No Results Found')}
                        labelMsg={t(
                          'You may need to configure your first source with this Route.',
                        )}
                      />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody className="text-body-sm text-ui-text-primary-text">
                  {sources.map((source: Source) => {
                    return (
                      <tr key={source.id} className={tableClasses.row}>
                        <td className={tableClasses.cell}>
                          <span className="inline-flex flex-wrap items-center gap-2">
                            {source.status === 'healthy' ? (
                              ''
                            ) : (
                              <AttentionIcon />
                            )}
                            <button
                              type="button"
                              onClick={() => setDrawerSource(source)}
                              className="cursor-pointer text-left text-ui-cta-primary hover:underline"
                              data-testid={`RouteDetailsSources-view-${source.external_id}`}
                            >
                              {source.external_id}
                            </button>
                            <ManualBadge
                              source={source}
                              testIdPrefix="RouteDetailsSources"
                              colorClassName="text-greyscale-6"
                            />
                          </span>
                        </td>
                        <td className={tableClasses.cell}>
                          {source.name || ''}
                        </td>
                        <td className={tableClasses.cell}>
                          {source.destinations.map((dest: Connection) => {
                            let dest_url: string;
                            try {
                              dest_url = new URL(dest.base_url).hostname;
                            } catch (err) {
                              dest_url = '';
                            }
                            return (
                              <div
                                key={dest.id}
                                className="flex items-center gap-2 text-ui-cta-primary"
                              >
                                {/* The hostname has always been drawn in link colour; it
                                                                now goes where every other "open this destination from
                                                                a Route" click goes — the account on the flow map. */}
                                <button
                                  type="button"
                                  data-testid={`RouteDetailsSources-destination-${dest.id}`}
                                  className="cursor-pointer text-left hover:underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onViewDestination(dest.id);
                                  }}
                                >
                                  {dest_url}
                                </button>
                                {dest.base_url && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(
                                        dest.base_url,
                                      );
                                      toast.info(
                                        `${t('URL')} ${t('copied to clipboard')}`,
                                        {
                                          position: toast.POSITION.TOP_RIGHT,
                                          autoClose: 2000,
                                        },
                                      );
                                    }}
                                    className="flex items-center justify-center opacity-50 transition-opacity hover:opacity-100"
                                    aria-label="Copy URL to clipboard"
                                  >
                                    <CopyToClipboard />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </td>
                        <td className={tableClasses.cell}>
                          {formatSourceDatetime(source.created_at, timezone)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
          {totalSources > page_size && (
            <Pagination
              showText={true}
              handlePageClick={onPageClicked}
              pageCount={Math.ceil(totalSources / take)}
              resultStart={offset + 1}
              resultEnd={
                offset + take < totalSources ? offset + take : totalSources
              }
              totalCount={totalSources}
              take={take}
              loading={!pageLoaded}
              mainclassname={paginationClasses.container}
            />
          )}
        </div>
      </div>
      <SourceDrawer
        isOpen={drawerSource !== null}
        onClose={() => setDrawerSource(null)}
        source={drawerSource}
      />
      {onCreateSource && (
        <AddDeviceModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          onCreate={onCreateSource}
          testIdPrefix="RouteDetailsSources"
        />
      )}
    </div>
  );
};

export default RouteDetailsSource;
